import * as Sentry from '@sentry/react';

/**
 * @param options Caller-provided overrides merged onto the shared defaults.
 * @param init The function that actually initializes Sentry. Defaults to
 *   `@sentry/react`'s `Sentry.init`. A native shell (practice app, Phase 82
 *   D-01) can pass `@sentry/capacitor`'s `init` instead, which wraps
 *   `@sentry/react`'s init so native and web events share one client/scope.
 *   Web callers pass nothing and keep today's behavior unchanged.
 */
export function initSentry(
  options?: Sentry.BrowserOptions,
  init: (options: Sentry.BrowserOptions) => void = Sentry.init,
): void {
  if (typeof window === 'undefined') {
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    return;
  }

  init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: 0.1,
    ignoreErrors: [
      // Google Translate and browser extensions mutating the DOM
      "Failed to execute 'removeChild' on 'Node'",
      "Failed to execute 'insertBefore' on 'Node'",
      "Failed to execute 'appendChild' on 'Node'",
      // Common browser extension noise
      'ResizeObserver loop',
      // Network errors not actionable on client
      'Load failed',
      'Failed to fetch',
      'NetworkError',
    ],
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
    ],
    ...options,
  });
}
